import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Link as MuiLink,
  Tabs,
  Tab,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import config from "../common/Config";
import * as XLSX from 'xlsx';
import "../common/commonStyles.css";
import { toast } from 'sonner';
import { getXeroPushErrorMessage, isXeroPushFailed } from '../../utils/xeroPushMessage';

const getTodayReportDefaults = () => {
  const today = new Date();
  const todayStr = format(today, 'dd/MM/yyyy');
  const reportrange = `${todayStr} - ${todayStr}`;
  return {
    dates: [today, today],
    reportrange,
  };
};

const PushCreditNoteReport = () => {
  const todayDefaults = getTodayReportDefaults();
  const [reportrange, setReportrange] = useState(todayDefaults.reportrange);
  const [dates, setDates] = useState(todayDefaults.dates);
  const [creditRows, setCreditRows] = useState([]);
  const [totals, setTotals] = useState({
    netAmount: 0,
    taxTotal: 0,
    grandTotal: 0,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activePushTab, setActivePushTab] = useState('not_pushed');
  const [pushingReturnIds, setPushingReturnIds] = useState({});
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');
  const currencySymbol = localStorage.getItem("currencySymbol") || '';
  const company_tax_type = localStorage.getItem("company_tax_type") || 'Tax';

  const handleDateChange = (value) => {
    setDates(value);
    if (value && value[0] && value[1]) {
      const startDate = format(value[0], 'dd/MM/yyyy');
      const endDate = format(value[1], 'dd/MM/yyyy');
      setReportrange(startDate + ' - ' + endDate);
      setPage(0);
    }
  };

  const fetchReport = useCallback(async () => {
    if (!reportrange) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('draw', 1);
      formData.append('start', page * rowsPerPage);
      formData.append('length', rowsPerPage);
      formData.append('searchValue', searchQuery);
      formData.append('reportrange', reportrange);
      formData.append('is_push', activePushTab === 'pushed' ? 'Y' : 'N');

      const response = await fetch(`${config.API_URL}/pushxerocredit`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to fetch report');
      const json = await response.json();

      if (json && json.data && Array.isArray(json.data)) {
        setCreditRows(json.data);
        setTotalCount(json.recordsTotal || json.data.length);
        setTotals({
          netAmount: parseFloat(json.sum_total_net || 0),
          taxTotal: parseFloat(json.sum_total_tax || 0),
          grandTotal: parseFloat(json.sum_grand_total || 0),
        });
      } else {
        setCreditRows([]);
        setTotalCount(0);
        setTotals({ netAmount: 0, taxTotal: 0, grandTotal: 0 });
      }
    } catch {
      setCreditRows([]);
      setTotalCount(0);
      setTotals({ netAmount: 0, taxTotal: 0, grandTotal: 0 });
    } finally {
      setLoading(false);
    }
  }, [accessToken, token, page, rowsPerPage, reportrange, searchQuery, activePushTab]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
  }, []);

  useEffect(() => {
    if (!reportrange) return;
    fetchReport();
  }, [reportrange, page, rowsPerPage, searchQuery, activePushTab, fetchReport]);

  const searchFormData = () => {
    if (dates[0] && dates[1]) {
      const startDate = format(dates[0], 'dd/MM/yyyy');
      const endDate = format(dates[1], 'dd/MM/yyyy');
      setReportrange(startDate + ' - ' + endDate);
      setPage(0);
    }
  };

  const exportToExcel = async () => {
    const formData = new FormData();
    formData.append('draw', '1');
    formData.append('start', '0');
    formData.append('length', totalCount || 10000);
    formData.append('searchValue', searchQuery);
    formData.append('reportrange', reportrange);
    formData.append('is_push', activePushTab === 'pushed' ? 'Y' : 'N');

    try {
      const response = await fetch(`${config.API_URL}/pushxerocredit`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      const exportData = (data.data || []).map((row) => ({
        Date: row.date || '',
        'Customer (A/C)': row.company
          ? `${row.company} (${row.accound_no || ''})`
          : row.accound_no || '',
        'Return No': row.reference_no || row.return_no || '',
        'Net Amount': parseFloat(row.net_amount || 0),
        [`${company_tax_type} Total`]: parseFloat(row.total_tax || 0),
        'Grand Total': parseFloat(row.grand_total || 0),
        'Xero Push Date': row.xero_push_date || '',
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Push Credit Note');
      const now = new Date();
      const formattedDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const formattedTime = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
      const statusLabel = activePushTab === 'pushed' ? 'Pushed' : 'Not-Pushed';
      XLSX.writeFile(wb, `Push-Credit-Note-${statusLabel}-${formattedDate}-${formattedTime}.xlsx`);
    } catch {
    }
  };

  const pushXeroCreditByReturn = async (returnId) => {
    if (!returnId) {
      toast.error('Return id not found');
      return;
    }

    setPushingReturnIds((prev) => ({ ...prev, [returnId]: true }));
    try {
      const formData = new FormData();
      formData.append('return_id', returnId);
      formData.append('accountCode', '200');
      formData.append('status', 'AUTHORISED');
      formData.append('lineAmountTypes', 'Exclusive');

      const response = await fetch(`${config.API_URL}/ws-push-xero-credit-by-return`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      let json = null;
      try {
        json = await response.json();
      } catch {
        json = null;
      }

      if (isXeroPushFailed(response, json)) {
        throw new Error(
          getXeroPushErrorMessage(json, 'Failed to push credit note')
        );
      }

      const successMsg = String(json?.message ?? '').trim();
      toast.success(
        successMsg &&
          successMsg.toLowerCase() !== 'false' &&
          successMsg.toLowerCase() !== 'true'
          ? successMsg
          : 'Credit note pushed'
      );
      await fetchReport();
    } catch (error) {
      toast.error(
        error?.message ||
          getXeroPushErrorMessage(null, 'Failed to push credit note')
      );
    } finally {
      setPushingReturnIds((prev) => ({ ...prev, [returnId]: false }));
    }
  };

  const formatNumber = (num) => parseFloat(num || 0).toFixed(2);

  const rowReference = (row) =>
    row.reference_no || row.return_no || row.credit_note_no || '';

  return (
    <Box className="page-container">
      <Paper className="paper-style">
        <Box sx={{ px: 2, py: 1.2 }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, gap: 2 }}>
            <Typography className="page-title">Push Credit Note Report</Typography>
            <Button
              variant="contained"
              className="button-variant-contained"
              href={`${config.API_URL}/api/xero/connect?tenant=${config.PREFIX || accessToken || ''}`}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Iconify icon="eva:link-2-fill" width={18} />}
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Connect Xero
            </Button>
          </Box>
          <Tabs className="common-tabs" sx={{ my: 1 }} value={activePushTab} onChange={(event, newValue) => { setActivePushTab(newValue); setPage(0); }}>
            <Tab label="Not Pushed Credit Note" value="not_pushed" />
            <Tab label="Pushed Credit Note" value="pushed" />
          </Tabs>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <TextField
                className="input-border"
                fullWidth
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                size="small"
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-outline" width={20} sx={{ color: "#637381" }} /></InputAdornment>,
                    endAdornment: searchQuery && <InputAdornment position="end"><IconButton size="small" onClick={() => { setSearchQuery(''); setPage(0); }} sx={{ color: "#637381" }}><Iconify icon="eva:close-fill" width={20} /></IconButton></InputAdornment>,
                  },
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <CommonDatePicker className="input-border" value={dates} onChange={handleDateChange} format="dd/MM/yyyy" character=" - " />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 2 }} display="flex" alignItems="flex-end">
              <Stack direction="row" spacing={1} width="100%">
                <Button variant="contained" onClick={searchFormData} fullWidth className="button-variant-contained">Submit</Button>
                <Button variant="outlined" onClick={exportToExcel} fullWidth className="button-variant-outlined" startIcon={<Iconify icon="eva:download-outline" width={20} />}>Export</Button>
              </Stack>
            </Grid2>
          </Grid2>
        </Box>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={40} /></Box>
        ) : (
          <TableContainer>
            <Table size="medium" className="table-cell-border" sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow className="table-cell-header">
                  <TableCell width="60px">Sr.no</TableCell>
                  <TableCell width="100px">Date</TableCell>
                  <TableCell width="350px">Customer (A/C)</TableCell>
                  <TableCell width="150px">Return. Ref</TableCell>
                  <TableCell width="120px" align="right">Net. Amount</TableCell>
                  <TableCell width="120px" align="right">{company_tax_type}. Total</TableCell>
                  <TableCell width="120px" align="right">Gross. Total</TableCell>
                  <TableCell width="140px" align="center">
                    {activePushTab === 'pushed' ? 'Xero Push Date' : 'Push'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {creditRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" sx={{ color: "#212b36" }}>{reportrange ? "No data available" : "Please select a date range and click Submit"}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  creditRows.map((row, index) => {
                    const returnId = row?.id ?? row?.return_id;
                    const isPushing = !!pushingReturnIds?.[returnId];
                    const ref = rowReference(row);
                    return (
                    <TableRow key={returnId ?? index} className="table-cell-style" sx={{ "&:last-child td": { borderBottom: "none" } }}>
                      <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
                      <TableCell>{row.date || ""}</TableCell>
                      <TableCell>{row.company ? `${row.company} (${row.accound_no || ''})` : ""}</TableCell>
                      <TableCell>
                        {returnId ? (
                          <MuiLink href={`${config.BASE_URL}/returns/view/${returnId}`} target="_blank" rel="noopener noreferrer" sx={{ color: "#212b36", textDecoration: "underline !important", "&:hover": { textDecoration: "underline !important" } }}>
                            {ref}
                          </MuiLink>
                        ) : (
                          ref
                        )}
                      </TableCell>
                      <TableCell sx={{ textAlign: "right" }}>{currencySymbol} {formatNumber(row.net_amount)}</TableCell>
                      <TableCell sx={{ textAlign: "right" }}>{currencySymbol} {formatNumber(row.total_tax)}</TableCell>
                      <TableCell sx={{ textAlign: "right" }}>{currencySymbol} {formatNumber(row.grand_total)}</TableCell>
                      <TableCell align="center">
                        {activePushTab === 'pushed' ? (
                          row.xero_push_date || "-"
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            disabled={isPushing}
                            onClick={() => pushXeroCreditByReturn(returnId)}
                            className="button-variant-contained"
                          >
                            {isPushing ? 'Pushing...' : 'Push'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ "& .MuiTableCell-root": { fontSize: "0.75rem", fontWeight: 600, color: "#212b36", py: 1.2, textAlign: "right", backgroundColor: "#f5f7fa", borderBottom: "none" } }}>
                  <TableCell colSpan={4} sx={{ borderBottom: "none" }} />
                  <TableCell>{currencySymbol} {formatNumber(totals.netAmount)}</TableCell>
                  <TableCell>{currencySymbol} {formatNumber(totals.taxTotal)}</TableCell>
                  <TableCell>{currencySymbol} {formatNumber(totals.grandTotal)}</TableCell>
                  <TableCell sx={{ borderBottom: "none" }} />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
        {!loading && creditRows.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", borderTop: "1px solid rgba(145, 158, 171, 0.16)" }}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to}${count !== -1 ? ` of ${count}` : ''}`}
              className="pagination-styles"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PushCreditNoteReport;
